/**
 * API client (axios) — talks to FastAPI backend.
 *
 * Base URL picked from VITE_API_URL env var.
 * - local dev:    http://localhost:8080
 * - prod:         https://api-one.encontreumnerd.com.br
 *
 * Auth flow:
 *  1. Request interceptor injeta Bearer do Zustand store (single source of truth).
 *  2. Response interceptor: em 401, tenta /v1/auth/refresh; se falhar, limpa session.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { useAuth } from "@/stores/auth";

const baseURL = import.meta.env.VITE_API_URL ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: false, // backend usa Bearer, não cookies
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ── Request interceptor: anexa Bearer do Zustand store ─────────────────────
api.interceptors.request.use((config) => {
  const tokens = useAuth.getState().tokens;
  if (tokens?.access_token) {
    config.headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  return config;
});

// ── Refresh flow: dedup em flight pra evitar race em N requests paralelas ──
interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const tokens = useAuth.getState().tokens;
  if (!tokens?.refresh_token) return null;

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      // Chamada sem interceptor pra evitar recursão
      const response = await axios.post<RefreshResponse>(
        `${baseURL}/v1/auth/refresh`,
        { refresh_token: tokens.refresh_token },
        { headers: { "Content-Type": "application/json" } },
      );
      const newTokens = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_in: response.data.expires_in,
      };
      const user = useAuth.getState().user;
      if (user) {
        useAuth.getState().setSession(user, newTokens);
      }
      return newTokens.access_token;
    } catch {
      // refresh falhou — derruba session
      useAuth.getState().clear();
      // hard reload pra ProtectedRoute redirecionar pra /login
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// ── Response interceptor: retry em 401 com refresh token ───────────────────
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    // /v1/auth/refresh em si: se 401, abandona (não tenta refresh do refresh)
    const isAuthEndpoint = original?.url?.includes("/v1/auth/");

    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const newAccess = await refreshAccessToken();
      if (newAccess) {
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api.request(original);
      }
    }
    return Promise.reject(error);
  },
);

// ── Endpoint typed helpers ─────────────────────────────────────────────────
export type HealthOk = { status: string };
export type VersionInfo = {
  service: string;
  version: string;
  env: string;
  region: string;
};

export async function getHealth(): Promise<HealthOk> {
  const { data } = await api.get<HealthOk>("/livez");
  return data;
}

export async function getVersion(): Promise<VersionInfo> {
  const { data } = await api.get<VersionInfo>("/version");
  return data;
}
